import styled from '@emotion/styled';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { Icon, Logo } from '@travelnest/hui';

import { openChatInsteadOfLink } from '@/components/support/HubSpotChat';
import {
	colors,
	focusRing,
	font,
	mq,
	mqMax,
	radii,
	spacing,
} from '@/lib/theme';
import { useI18n } from '@/locales';

const Bar = styled.header`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${spacing.space40};
	min-height: 64px;
	padding: ${spacing.space30} 0;

	${mq.md} {
		min-height: 72px;
	}
`;

const Slot = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacing.space30};
	min-width: 0;
`;

interface SiteHeaderProps {
	/** Left-hand content: usually `HostedBy` or `BackLink`. */
	start?: ReactNode;
	/** Right-hand content: usually `HelpLink`, plus a language switcher where needed. */
	end?: ReactNode;
}

/** The slim bar at the top of every page. Pages choose what goes in each slot. */
export const SiteHeader = ({ start, end }: SiteHeaderProps) => (
	<Bar>
		<Slot>{start}</Slot>
		<Slot>{end}</Slot>
	</Bar>
);

/** The header's rounded control: a link by default, `PillButton` for actions. */
export const Pill = styled.a`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.space10};
	height: 36px;
	padding: 0 ${spacing.space30} 0 ${spacing.space20};
	border-radius: 999px;
	background: ${colors.LM_NU0};
	border: 1px solid ${colors.interface.border};
	${font('IM90')}
	color: ${colors.typography.heading};
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 150ms ease-out;

	&:hover {
		background: ${colors.LM_NU20};
	}
	&:focus-visible {
		${focusRing}
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background: ${colors.LM_NU0};
	}

	svg {
		color: ${colors.LM_NU70};
	}
`;

export const PillButton = Pill.withComponent('button');

/* Phones: just the icon, in a circle the height of the pill; the label stays for screen readers. */
const CompactPill = styled(Pill)`
	${mqMax.md} {
		width: 36px;
		padding: 0;
		justify-content: center;

		span {
			position: absolute;
			width: 1px;
			height: 1px;
			overflow: hidden;
			clip-path: inset(50%);
			white-space: nowrap;
		}
	}
`;

const HostedByLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.space20};
	${font('IR90')}
	color: ${colors.typography.body};
	white-space: nowrap;

	strong {
		display: inline-flex;
		align-items: center;
		gap: ${spacing.space10};
		font-weight: 500;
		color: ${colors.typography.heading};
	}

	&:hover strong {
		color: ${colors.typography.link};
	}
	&:focus-visible {
		${focusRing}
		border-radius: ${radii.radii20};
	}
`;

/** "Hosted by Travelnest", linking to the marketing site. */
export const HostedBy = () => {
	const t = useI18n();
	return (
		<HostedByLink
			href="https://travelnest.com"
			target="_blank"
			rel="noreferrer"
		>
			<span>{t('Header.hostedBy')}</span>
			<strong>
				<Logo type="logomark" variant="lightbg" height={20} />
				{t('common.Travelnest')}
			</strong>
		</HostedByLink>
	);
};

interface BackLinkProps {
	href?: string;
	onClick?: () => void;
	/** A button that can't be used for now (a payment is going through). */
	disabled?: boolean;
	children: ReactNode;
}

/** A pill with a chevron that takes the guest back a step. */
export const BackLink = ({
	href,
	onClick,
	disabled,
	children,
}: BackLinkProps) =>
	href ? (
		<Pill as={Link} href={href}>
			<Icon name="ChevronLeft" size={24} />
			{children}
		</Pill>
	) : (
		<PillButton type="button" onClick={onClick} disabled={disabled}>
			<Icon name="ChevronLeft" size={24} />
			{children}
		</PillButton>
	);

interface HelpLinkProps {
	propertyId?: string;
	/** Icon only on phones, where it shares a row with other controls. */
	compact?: boolean;
}

/** Opens the support form in a new tab, pre-filled with the property. */
export const HelpLink = ({ propertyId, compact = false }: HelpLinkProps) => {
	const t = useI18n();
	const href = propertyId
		? `/support?property_id=${encodeURIComponent(propertyId)}`
		: '/support';
	const Control = compact ? CompactPill : Pill;
	return (
		<Control
			href={href}
			target="_blank"
			rel="noreferrer"
			onClick={openChatInsteadOfLink}
		>
			<Icon name="HelpCircle" size={24} />
			<span>{t('Header.help')}</span>
		</Control>
	);
};
